import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_ImageSharpener = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/image-sharpener',
			name: 'AI Image Sharpener | Lift',
			description:
				'AI image sharpener online. Sharpen blurry photos instantly, increase image quality, and improve picture clarity. No watermarks.',
			url: 'https://liftapp.ai/image-sharpener',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/image-sharpener#product' },
				{ '@id': 'https://liftapp.ai/image-sharpener#howto' },
				{ '@id': 'https://liftapp.ai/image-sharpener#benefits' },
				{ '@id': 'https://liftapp.ai/image-sharpener#faq' },
				{ '@id': 'https://liftapp.ai/image-sharpener#why-lift' },
				{ '@id': 'https://liftapp.ai/image-sharpener#explore-tools' },
				{ '@id': 'https://liftapp.ai/image-sharpener#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/image-sharpener#product',
			name: 'AI Image Sharpener',
			description:
				'AI image sharpener online — sharpen blurry photos instantly, increase image quality, and improve picture clarity. No watermarks.',
			url: 'https://liftapp.ai/image-sharpener',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: 4.8,
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/image-sharpener#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload your photos',
					text: 'Choose a photo from your device or take one using Lift’s app',
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Let AI uncrop it',
					text: 'Our AI analyzes your photo, understands its composition, and seamlessly extends the background while keeping every detail natural',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Customize adjustments',
					text: 'Fine-tune your image with easy-to-use editing tools',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download and use',
					text: 'Download your image or publish it straight to your social feeds',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/image-sharpener#benefits',
			name: 'Who can benefit from AI Image Sharpener?',
			description:
				'The AI Image Sharpener is perfect for anyone who wants to fix awkwardly cropped photos, social media users, content creators, friends and couples, and e-commerce sellers.',
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'AI Image Sharpener is a tool for anyone who needs sharp, professional-quality photos',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Content creators and influencers for professional-looking posts',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Businesses and e-commerce sellers improving product visuals',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Marketers elevating ads and social media',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Event planners preserving memories in crisp detail',
				},
				{
					'@type': 'ListItem',
					position: 6,
					name: 'Students or professionals can clarify scanned documents, screenshots, or images',
				},
				{
					'@type': 'ListItem',
					position: 7,
					name: 'Photographers can restore older or low-quality images effortlessly',
				},
				{
					'@type': 'ListItem',
					position: 8,
					name: 'AI Photo Sharpener bridges the gap between quick fixes and professional-quality results, making it indispensable for both personal and professional use',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/landing_2.3cdd75c1.webp',
				name: 'Before and After: Image Sharpening Demonstration',
				description:
					'Before and after comparison of the Golden Gate Bridge photo.',
			},
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/image-sharpener#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is Lift AI Image Sharpener?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift AI Image Sharpener is a premium online tool that improves photo clarity, colour and resolution up to 4K in a single click.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does Lift AI Photo Sharpener work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Our AI understands your photo’s colors, textures, and perspective, then generates realistic extensions that blend seamlessly. Everything runs in the cloud — no manual editing required.',
					},
				},
				{
					'@type': 'Question',
					name: 'Will it keep the original look of my photo?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. The AI preserves your photo’s style, lighting, and composition, so the expanded area looks like it was always part of the picture.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download the results without a watermark?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Absolutely. All images generated or edited in Lift can be exported without watermark when you're on the Pro plan. Download them in high-res PNG or JPG — ready for social media, print, ads, or e-commerce.",
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/image-sharpener#why-lift',
			name: 'Why Lift’s AI Image Sharpener stands out',
			description:
				"Lift's AI Image Sharpener provides professional results, AI accuracy, cross-platform support, and is budget-friendly.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Professional results',
					description:
						'Achieve studio-quality images without the need for complex tools',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'AI accuracy',
					description:
						'Smart algorithms deliver enhancements tailored to your specific image',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly editing services with Lift’s affordable solution',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/landing_3.eb14fd6d.webp',
				name: 'Bring Food to Life: Image Sharpening Example',
				description:
					'Split image: blurry photo of kimchi fried rice on left, sharp and clear photo on right.',
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/image-sharpener#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/image-sharpener#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in/?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in/?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Image Sharpener',
					item: 'https://liftapp.ai/image-sharpener',
				},
			],
		},
	],
};
